import type { ResourceName } from '@rex-mortis/shared';
import { RESOURCE_LABELS } from '@rex-mortis/shared';
import { formatBigNum } from '../util/formatBig.js';

/**
 * Monochrome unicode glyph per resource. U+FE0E variation selector forces
 * text-style rendering on platforms that would otherwise pick a colour emoji.
 * Each glyph inherits the bar's accent colour via CSS, so the symbol
 * automatically matches its resource hue in both light and dark theme.
 */
const RESOURCE_GLYPH: Record<ResourceName, string> = {
  vigor:   '⚔︎',  // ⚔︎ — crossed swords (exertion)
  resolve: '⛨︎',  // ⛨︎ — shield with cross (willpower)
  spirit:  '✦︎',  // ✦ — four-point star (soul)
  life:    '♥︎',  // ♥︎ — heart (HP)
};

/**
 * Regen rates per second. Must stay in sync with server/src/game/balance.ts
 * (REGEN constant). Life is a fraction of lifeMax per second (not flat points).
 */
const FLAT_PER_SEC: Record<Exclude<ResourceName, 'life'>, number> = {
  vigor:   5 / (4 * 60),   // ~1h 24m to fill default 105
  resolve: 1 / 70,         // ~30m to fill default 26
  spirit:  1 / 32,         // ~2h 40m to fill default 300
};
const LIFE_FRACTION_PER_SEC = 0.05 / 60; // ~20m to fill 0→100%

function formatCountdown(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return 'Full';
  const s = Math.ceil(seconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `Full in ${h}h ${m}m`;
  if (m > 0) return `Full in ${m}m ${sec}s`;
  return `Full in ${sec}s`;
}

export function ResourceBar(opts: {
  kind: ResourceName;
  current: number;
  max: number;
  /** Epoch-ms when `current` was measured by the server. The bar predicts
   *  forward from THIS anchor, not from when the element was built — so a header
   *  rebuild mid-session keeps the accrued regen instead of snapping back to the
   *  stale baseline. Defaults to now() for callers without a server timestamp. */
  asOf?: number;
}): HTMLElement {
  const { kind, current, max } = opts;
  const pct = max > 0 ? Math.min(100, Math.max(0, (current / max) * 100)) : 0;

  const wrap = document.createElement('div');
  wrap.className = `resbar ${kind}`;

  const label = document.createElement('div');
  label.className = 'label';

  const left = document.createElement('span');
  left.style.display = 'inline-flex';
  left.style.alignItems = 'center';
  left.style.gap = '6px';
  const icon = document.createElement('span');
  icon.className = 'resbar-glyph';
  icon.textContent = RESOURCE_GLYPH[kind];
  icon.setAttribute('aria-hidden', 'true');
  const labelText = document.createElement('span');
  labelText.className = 'name';
  labelText.textContent = RESOURCE_LABELS[kind];
  left.append(icon, labelText);

  // Thousands-separated, integer figures so large caps stay legible
  // (e.g. "1,250,000 / 2,500,000") and never run together.
  const fmt = (n: number): string => Math.floor(n).toLocaleString('en-US');
  // Short form ("30.6K") for values >= 10,000 — shown only on narrow/mobile
  // viewports via CSS, where the full comma figure would truncate the bar.
  // Below 10,000 the short form is just the full figure, so small bars read
  // naturally ("6 / 1,170") on every screen.
  const fmtShort = (n: number): string =>
    Math.floor(n) >= 10_000 ? formatBigNum(Math.floor(n)) : fmt(n);

  const right = document.createElement('span');
  right.className = 'count';
  const countFull = document.createElement('span');
  countFull.className = 'count-full';
  const countShort = document.createElement('span');
  countShort.className = 'count-short';
  right.append(countFull, countShort);
  const setCounts = (cur: number, mx: number): void => {
    countFull.textContent = `${fmt(cur)} / ${fmt(mx)}`;
    countShort.textContent = `${fmtShort(cur)} / ${fmtShort(mx)}`;
  };
  setCounts(current, max);

  label.append(left, right);

  const track = document.createElement('div');
  track.className = 'track';
  const fill = document.createElement('div');
  fill.className = 'fill';
  fill.style.width = `${pct}%`;
  track.append(fill);

  // Countdown line — recomputes every second so the value feels live.
  const timer = document.createElement('div');
  timer.className = 'timer';
  // Anchor prediction to when the server measured `current`, not to build time.
  // Clamp to "not in the future" so a slightly-skewed clock can't show negative
  // elapsed. This is what makes the bar survive header rebuilds without drift.
  const startedAt = Math.min(opts.asOf ?? Date.now(), Date.now());
  /** Points-per-second of regen for this resource at the time the bar was built. */
  const ratePerSec = kind === 'life'
    ? LIFE_FRACTION_PER_SEC * max
    : FLAT_PER_SEC[kind as Exclude<ResourceName, 'life'>];

  const updateTick = (): void => {
    let liveCurrent: number;
    let secsLeft: number;
    if (current >= max) {
      // At or ABOVE max. Overfill (elixir/tonic, past the cap) does NOT regen
      // down on its own — it persists until spent — so show the true value and
      // never tick it. This is what lets a 10,000 / 42 overfill actually appear
      // instead of being clamped to the cap.
      liveCurrent = current;
      secsLeft = 0;
    } else {
      // Below max: tick up at the regen rate, capped at max.
      const elapsed = (Date.now() - startedAt) / 1000;
      liveCurrent = Math.min(max, current + ratePerSec * elapsed);
      secsLeft = ratePerSec > 0 ? (max - liveCurrent) / ratePerSec : Infinity;
    }
    // Round for display — keep integer-feeling numbers. Overfill shows e.g.
    // "10,000 / 42"; a normal full bar shows "42 / 42".
    setCounts(liveCurrent, max);
    // Bar fill — capped at 100% (an overfilled bar simply reads full).
    const livePct = max > 0 ? Math.min(100, Math.max(0, (liveCurrent / max) * 100)) : 0;
    fill.style.width = `${livePct}%`;
    // Timer line: "Overfilled" above the cap, otherwise the fill countdown / "Full".
    if (current > max) {
      timer.textContent = 'Overfilled';
      timer.classList.add('full');
    } else {
      timer.textContent = formatCountdown(secsLeft);
      if (secsLeft <= 0) timer.classList.add('full');
      else timer.classList.remove('full');
    }
  };
  updateTick();
  const handle = window.setInterval(() => {
    // Stop ticking once the element is removed from the DOM (header rebuild).
    if (!wrap.isConnected) {
      window.clearInterval(handle);
      return;
    }
    updateTick();
  }, 1000);

  wrap.append(label, track, timer);
  return wrap;
}
